import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MyWebsiteDetail.css';

const sections = [
  {
    id: 'overview',
    type: 'split',
    reverse: false,
    num: '01',
    title: 'Project Overview',
    image: '/images/my home page.png',
    imageAlt: 'Homepage screenshot',
    body: [
      'My first website exists because I want to learn programming by doing. I chose JavaScript and React because JavaScript is the most widely used language on the front end, and has a huge ecosystem and community I can learn from.',
      'The current version is organized into four parts: a sign-in entry in the top bar, a language switcher, a sidebar that separates primary and secondary actions, and a main content area for pages. The earliest build was simple — just the sidebar and translation, focused on foundational finance concepts.',
    ],
  },
  {
    id: 'auth',
    type: 'carousel',
    reverse: true,
    num: '02',
    slides: [
      {
        title: 'Authentication System',
        image: '/images/welcome to login.png',
        imageAlt: 'Welcome login screenshot',
        body: [
          'The sign-in at the top right supports two paths: quick OAuth with Google, and a custom account using email and password. Offering both keeps things convenient for people who prefer one-click login, while still letting privacy-minded users keep credentials within this site.',
          'Passwords are never stored in plain text — they\'re protected with bcrypt. When you log in, the server issues a short-lived JWT and sets it in an httpOnly, same-site cookie so your session is remembered securely without exposing the token to front-end scripts.',
        ],
      },
      {
        title: 'Email Verification',
        image: '/images/user registration.png',
        imageAlt: 'User registration screenshot',
        body: [
          'I built the email system using Nodemailer with Gmail\'s SMTP service, mainly because it\'s easy to set up and perfect for a small project like this. When the server generates a verification code, it sends an email directly through Gmail using my test account credentials stored in environment variables.',
          'The message includes the six-digit code and a short note. I also made the email text multilingual, so it automatically matches the language you selected on the site (English, Traditional Chinese, or my beginner-level Japanese). It helped me understand how email transport, encryption, and environment-based secrets actually work in a real backend.',
        ],
      },
    ],
  },
  {
    id: 'db',
    type: 'full',
    num: '03',
    title: 'Database Choice',
    body: [
      'For this project I decided to use MongoDB with Mongoose rather than a relational database. At the start, my priority was to learn by building quickly, and MongoDB felt straightforward: the JSON-like document model maps directly to JavaScript objects.',
      'I do work with PostgreSQL in my main app, where relational structure and strong consistency are critical, but for this first learning project I deliberately chose MongoDB because it allowed me to focus on programming concepts, authentication flows, and user features.',
    ],
  },
  {
    id: 'i18n',
    type: 'split',
    reverse: false,
    num: '04',
    title: 'The Language Switcher',
    image: '/images/The language switcher.png',
    imageAlt: 'Language switcher screenshot',
    body: [
      'The language switcher is built with i18n (i18next + react-i18next) so the interface can change languages instantly without reloading. Growing up in Hong Kong — where English and Chinese are official languages — made bilingual access feel natural.',
      'Under the hood, translations live in small JSON files per language and namespace, are auto-detected via a browser language detector, and fall back to English if a key is missing. I\'m also experimenting with a Japanese (beginner/N5) option.',
    ],
  },
];

function AnimatedSection({ children, className }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible');
          observer.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

function CarouselSection({ section }) {
  const [index, setIndex] = useState(0);
  const total = section.slides.length;
  const slide = section.slides[index];

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(total - 1, i + 1));

  return (
    <AnimatedSection
      className={`mwd-section${section.reverse ? ' reverse' : ''}`}
    >
      <div className="mwd-section-text">
        <span className="mwd-section-num">{section.num}</span>

        <div className="mwd-carousel">
          <div className="mwd-carousel-track">
            {section.slides.map((s, i) => (
              <div
                key={i}
                className={`mwd-carousel-slide ${i === index ? 'active' : ''}`}
                aria-hidden={i !== index}
              >
                <h2 className="mwd-section-title">{s.title}</h2>
                <div className="mwd-section-body">
                  {s.body.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mwd-carousel-controls">
            <button
              className="mwd-carousel-btn"
              onClick={prev}
              disabled={index === 0}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              className="mwd-carousel-btn"
              onClick={next}
              disabled={index === total - 1}
              aria-label="Next"
            >
              ›
            </button>
            <div className="mwd-carousel-dots">
              {section.slides.map((_, i) => (
                <div
                  key={i}
                  className={`mwd-carousel-dot ${i === index ? 'active' : ''}`}
                />
              ))}
            </div>
            <span className="mwd-carousel-counter">
              {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
            </span>
          </div>
        </div>
      </div>

      <div className="mwd-section-image-wrap">
        <img src={slide.image} alt={slide.imageAlt} />
      </div>
    </AnimatedSection>
  );
}

export default function MyWebsiteDetail() {
  const navigate = useNavigate();

  return (
    <div className="mwd-page">
      <button className="mwd-back" onClick={() => navigate('/my-work')}>
        ← My Work
      </button>

      {/* ── Hero ── */}
      <div className="mwd-hero">
        <span className="mwd-hero-tag">Case Study · 2025</span>
        <h1 className="mwd-hero-title">My Website</h1>
        <p className="mwd-hero-desc">My first web application — built to learn by doing.</p>
        <div className="mwd-hero-techs">
          {['HTML', 'CSS', 'JavaScript', 'React', 'MongoDB', 'Vercel'].map((t) => (
            <span key={t} className="mwd-tech-pill">{t}</span>
          ))}
        </div>
      </div>

      {/* ── Sections ── */}
      {sections.map((s) => {
        if (s.type === 'full') {
          return (
            <AnimatedSection key={s.id} className="mwd-full">
              <span className="mwd-section-num">{s.num}</span>
              <h2 className="mwd-section-title">{s.title}</h2>
              <div className="mwd-section-body">
                {s.body.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </AnimatedSection>
          );
        }

        if (s.type === 'carousel') {
          return <CarouselSection key={s.id} section={s} />;
        }

        return (
          <AnimatedSection
            key={s.id}
            className={`mwd-section${s.reverse ? ' reverse' : ''}`}
          >
            <div className="mwd-section-text">
              <span className="mwd-section-num">{s.num}</span>
              <h2 className="mwd-section-title">{s.title}</h2>
              <div className="mwd-section-body">
                {s.body.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
            <div className="mwd-section-image-wrap">
              <img src={s.image} alt={s.imageAlt} />
            </div>
          </AnimatedSection>
        );
      })}

      {/* ── Footer ── */}
      <div className="mwd-footer">
        <div>
          <button className="mwd-footer-back" onClick={() => navigate('/my-work')}>
            ← Back to My Work
          </button>
        </div>
        <p>My Website · 2025</p>
      </div>
    </div>
  );
}
